import React, { useState } from 'react';
import { Formik } from 'formik';
import { useConfigContext, useTenantContext, useToastContext } from '../contexts';
import BlankModal from './BlankModal';
import { useIssuerService } from '../services';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  DimensionValue,
  ActivityIndicator
} from 'react-native';

interface INewUser {
  email: string;
  name: string;
}

export interface IUserList {
  name: string;
  email: string;
  role: string;
}

const newUserInitialValues: INewUser = {
  email: '',
  name: ''
};

//TODO: Add validation YUP Schema for form

export interface IOnboardUserModalProps {
  isVisible: boolean;
  onClose: () => void;
}

/**
 * OnboardUserModal component
 *
 * @param {*} { isVisible, onClose }
 * @return {*}
 */
const OnboardUsersModal: React.FC<IOnboardUserModalProps> = ({ isVisible, onClose }) => {
  const issuerService = useIssuerService();
  const tenantContext = useTenantContext();
  const toast = useToastContext();
  const configContext = useConfigContext();

  const usersToInvite = configContext.USERS || [];

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const createInvitations = async (users: IUserList[]) => {
    setIsLoading(true);
    for (const user of users) {
      await issuerService.registerNewUser({ email: user.email, name: user.name });
    }
    await tenantContext.refresh();
    toast.showToast('Users invited successfully', 3000, true);
    setSelectedUsers([]);
    onClose();
    setIsLoading(false);
  };

  const handleClose = (resetForm: () => void) => {
    onClose();
    resetForm();
    setSelectedUsers([]);
  };

  const toggleUserSelection = (email: string) => {
    setSelectedUsers((prev) => {
      if (prev.includes(email)) {
        return prev.filter((e) => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === usersToInvite.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(usersToInvite.map((user) => user.email));
    }
  };

  return (
    <Formik
      initialValues={newUserInitialValues}
      onSubmit={() => {
        const selectedUserData = usersToInvite.filter((user) => selectedUsers.includes(user.email));
        createInvitations(selectedUserData);
      }}>
      {(props) => (
        <BlankModal
          title="Invite Users"
          isVisible={isVisible}
          onClose={() => handleClose(props.resetForm)}
          onConfirm={props.handleSubmit}
          isLoading={isLoading}>
          {isLoading ? (
            <View style={styles.container}>
              <ActivityIndicator size="large" color="#0000ff" />
            </View>
          ) : (
            <View style={styles.container}>
              <View style={styles.header}>
                <Text style={styles.title}>Select Users to Invite</Text>
                <TouchableOpacity onPress={handleSelectAll} style={styles.selectAllButton}>
                  <Text style={styles.selectAllText}>
                    {selectedUsers.length === usersToInvite.length ? 'Deselect All' : 'Select All'}
                  </Text>
                </TouchableOpacity>
              </View>
              <ScrollView style={styles.userList}>
                {usersToInvite &&
                  usersToInvite.map((user) => (
                    <TouchableOpacity
                      key={user.email}
                      style={[
                        styles.userItem,
                        selectedUsers.includes(user.email) && styles.selectedUserItem
                      ]}
                      onPress={() => toggleUserSelection(user.email)}>
                      <View style={styles.userInfo}>
                        <Text style={styles.userName}>{user.name}</Text>
                        <Text style={styles.userRole}>{user.role}</Text>
                        <Text style={styles.userEmail}>{user.email}</Text>
                      </View>
                    </TouchableOpacity>
                  ))}
              </ScrollView>
            </View>
          )}
        </BlankModal>
      )}
    </Formik>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1a1a1a'
  },
  selectAllButton: {
    padding: 8,
    borderRadius: 4,
    backgroundColor: '#4B5563'
  },
  selectAllText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500'
  },
  userList: {
    flex: 1,
    maxHeight: '60vh' as DimensionValue
  },
  userItem: {
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    marginBottom: 8,
    backgroundColor: '#ffffff'
  },
  selectedUserItem: {
    backgroundColor: '#F3F4F6',
    borderColor: '#4B5563'
  },
  userInfo: {
    flex: 1
  },
  userName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4
  },
  userRole: {
    fontSize: 14,
    color: '#4B5563',
    marginBottom: 2
  },
  userEmail: {
    fontSize: 14,
    color: '#6B7280'
  }
});

export default OnboardUsersModal;
